import { useCallback, useMemo, useState } from "react";
import {
  FilterChangeCallBack,
  FilterInfo,
  PageChangeCallBack,
  PaginationControllerParams,
  PaginationControls,
  ResetFiltersCallback,
  SortChangeCallBack,
  SortInfo,
} from "types/pagination";

import { getSortString } from "./usePaginationUrlControls";

export default function usePaginationControls({
  defaultPageSize = 10,
  defaultSortInfo = {},
  defaultFilterInfo = {},
}: PaginationControllerParams = {}): PaginationControls {
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize || 10);
  const [sortInfo, setSortInfo] = useState<SortInfo>(defaultSortInfo || {});
  const [filterInfo, setFilterInfo] = useState<FilterInfo>(
    defaultFilterInfo || {},
  );

  const onPageChange: PageChangeCallBack = useCallback((page, pageSize) => {
    setPage(page);
    setPageSize(pageSize);
  }, []);

  const onSortChange: SortChangeCallBack = useCallback((sortInfo) => {
    setSortInfo(sortInfo);
  }, []);

  const onFilterChange: FilterChangeCallBack = useCallback((filterInfo) => {
    setPage(1);
    setFilterInfo(filterInfo);
  }, []);

  const resetFilters: ResetFiltersCallback = useCallback(
    ({ filters: initialFilters, sortInfo: initialSortInfo = {} } = {}) => {
      setPageSize(defaultPageSize);
      setSortInfo(initialSortInfo);
      setPage(1);
      setFilterInfo(initialFilters || {});
    },
    [defaultPageSize],
  );

  const sortString = getSortString(sortInfo);

  return useMemo(
    () => ({
      page,
      pageSize,
      sortInfo,
      sortString,
      filterInfo,
      onPageChange,
      onSortChange,
      onFilterChange,
      resetFilters,
      defaultPageSize,
    }),
    [
      page,
      pageSize,
      sortInfo,
      sortString,
      filterInfo,
      onPageChange,
      onSortChange,
      onFilterChange,
      resetFilters,
      defaultPageSize,
    ],
  );
}
